import { z } from "zod";
import { ulidSchema } from "./schemas.js";
export const SIMULATED_PRINT_NOTICE = "Simulation only. No order is sent to Lulu, Gelato, or another printer, and no physical shipment will occur.";
export const simulatedPrintSchemaVersionSchema = z.literal("2026-09-21.1");
export const simulatedPrintProviderSchema = z.literal("LIFESCAPSULE_PRINT_SIMULATOR");
export const simulatedPrintStateSchema = z.enum(["ACCEPTED", "SHIPPED", "DELIVERED"]);
export const simulatedPrintNotificationKindSchema = z.enum(["ACCEPTED", "SHIPPED", "DELIVERED"]);
export const simulatedPrintNotificationStateSchema = z.enum([
    "SCHEDULED",
    "CLAIMED",
    "SENT",
    "SUPPRESSED",
    "NEEDS_ATTENTION",
]);
export const simulatedPrintArtifactConfirmationSchema = z
    .object({
    kind: z.enum(["INTERIOR", "COVER"]),
    checksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
})
    .strict();
export const createSimulatedPrintCommandSchema = z
    .object({
    previewId: ulidSchema,
    confirmedSimulation: z.literal(true),
    confirmedNoPhysicalShipment: z.literal(true),
    artifacts: z.array(simulatedPrintArtifactConfirmationSchema).length(2),
})
    .strict()
    .superRefine((command, context) => {
    if (new Set(command.artifacts.map((artifact) => artifact.kind)).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "Confirm the exact interior and cover PDF checksums.",
        });
});
export const simulatedPrintNotificationSchema = z
    .object({
    kind: simulatedPrintNotificationKindSchema,
    state: simulatedPrintNotificationStateSchema,
    dueAt: z.string().datetime(),
    attemptedAt: z.string().datetime().optional(),
    sentAt: z.string().datetime().optional(),
})
    .strict();
export const simulatedPrintFulfillmentSchema = z
    .object({
    schemaVersion: simulatedPrintSchemaVersionSchema,
    id: ulidSchema,
    bookId: ulidSchema,
    previewId: ulidSchema,
    provider: simulatedPrintProviderSchema,
    state: simulatedPrintStateSchema,
    simulation: z.literal(true),
    test: z.literal(true),
    physicalShipment: z.literal(false),
    notice: z.literal(SIMULATED_PRINT_NOTICE),
    acceptedAt: z.string().datetime(),
    shipScheduledAt: z.string().datetime(),
    deliveryScheduledAt: z.string().datetime(),
    shippedAt: z.string().datetime().optional(),
    deliveredAt: z.string().datetime().optional(),
    simulatedTrackingReference: z.string().regex(/^SIM-[0-9A-HJKMNP-TV-Z]{26}$/u),
    artifacts: z.array(simulatedPrintArtifactConfirmationSchema).length(2),
    notifications: z.array(simulatedPrintNotificationSchema).length(3),
})
    .strict()
    .superRefine((fulfillment, context) => {
    if (new Set(fulfillment.artifacts.map((artifact) => artifact.kind)).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["artifacts"],
            message: "Artifacts must be unique.",
        });
    if (new Set(fulfillment.notifications.map((entry) => entry.kind)).size !== 3)
        context.addIssue({
            code: "custom",
            path: ["notifications"],
            message: "Every simulated notification must be represented once.",
        });
    if (Date.parse(fulfillment.shipScheduledAt) < Date.parse(fulfillment.acceptedAt) + 600_000)
        context.addIssue({
            code: "custom",
            path: ["shipScheduledAt"],
            message: "Simulated shipment cannot occur before ten minutes.",
        });
    if (Date.parse(fulfillment.deliveryScheduledAt) <
        Date.parse(fulfillment.shipScheduledAt) + 600_000)
        context.addIssue({
            code: "custom",
            path: ["deliveryScheduledAt"],
            message: "Simulated delivery cannot occur before another ten minutes.",
        });
    if (fulfillment.state === "ACCEPTED" && (fulfillment.shippedAt || fulfillment.deliveredAt))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "An accepted simulation cannot carry shipment timestamps.",
        });
    if (fulfillment.state === "SHIPPED" && (!fulfillment.shippedAt || fulfillment.deliveredAt))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A shipped simulation requires only its shipment timestamp.",
        });
    if (fulfillment.state === "DELIVERED" && (!fulfillment.shippedAt || !fulfillment.deliveredAt))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A delivered simulation requires shipment and delivery timestamps.",
        });
    if (fulfillment.shippedAt &&
        Date.parse(fulfillment.shippedAt) < Date.parse(fulfillment.shipScheduledAt))
        context.addIssue({
            code: "custom",
            path: ["shippedAt"],
            message: "A simulated shipment cannot be recorded before its scheduled boundary.",
        });
    if (fulfillment.deliveredAt &&
        (Date.parse(fulfillment.deliveredAt) < Date.parse(fulfillment.deliveryScheduledAt) ||
            (fulfillment.shippedAt &&
                Date.parse(fulfillment.deliveredAt) < Date.parse(fulfillment.shippedAt))))
        context.addIssue({
            code: "custom",
            path: ["deliveredAt"],
            message: "A simulated delivery cannot be recorded before shipment or its due boundary.",
        });
});
export const simulatedPrintFulfillmentResponseSchema = z
    .object({ data: simulatedPrintFulfillmentSchema })
    .strict();
export const simulatedPrintFulfillmentListResponseSchema = z
    .object({
    data: z.array(simulatedPrintFulfillmentSchema),
    simulation: z.literal(true),
    physicalShipment: z.literal(false),
    notice: z.literal(SIMULATED_PRINT_NOTICE),
})
    .strict();
//# sourceMappingURL=simulated-print.js.map